import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { Redux } from '../..'
import {
  UpdateEquipmentContact,
  UpdateEquipment,
  EquipmentCategory,
  EquipmentMaster,
  EquipmentBrand,
  EquipmentModel,
  EquipmentResponse,
  EquipmentResponseById,
  EquipmentCategoriesResponse,
  EquipmenModelDataResponse,
  CreateEquipment,
  UpdateEquipmentData,
  EquipmentContactResponse,
  EquipmentContact,
  EquipmentContactResponseById,
  UpdateEquipmentContactData,
  EquipmentMasterDataResponse,
  EquipmentSupplierDataResponse,
  EquipmentBrandDataResponse
} from '@/src/types/apps/gygEquipment'

interface EquipmentInitialState {
  equipmentList: UpdateEquipment[]
  equipment: UpdateEquipment | null
  contact: UpdateEquipmentContact | null
  loading: boolean
  success: string | null
  error: string | null
  categoryList: EquipmentCategory[]
  masterEquipmentList: EquipmentMaster[]
  brandList: EquipmentBrand[]
  modelList: EquipmentModel[]
  supplierList: any[]
  contactList: UpdateEquipmentContact[]
}

const initialState: EquipmentInitialState = {
  equipmentList: [],
  contactList: [],
  equipment: null,
  contact: null,
  categoryList: [],
  masterEquipmentList: [],
  brandList: [],
  modelList: [],
  supplierList: [],
  loading: false,
  error: null,
  success: null
}

interface EquipmentQueryOptions {
  take?: number
  sortBy?: string
  restaurantId?: string
}

export const getEquipmentList = createAsyncThunk<EquipmentResponse, string, Redux>(
  'gygEquipmentList/getEquipmentList',
  async (restaurantId: string, { rejectWithValue, extra }) => {
    try {
      const response = await extra.httpClient.get(`resto365-equipments?restaurantId=${restaurantId}`)

      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const getEquipmentCategoryList = createAsyncThunk<
  EquipmentCategoriesResponse,
  void,
  { rejectValue: any; extra: { httpClient: any } }
>('gygEquipmentList/getEquipmentCategories', async (_, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.get(`resto365-equipment-categories`)

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const getEquipmentMasterList = createAsyncThunk<
  EquipmentMasterDataResponse,
  EquipmentQueryOptions,
  { rejectValue: any; extra: { httpClient: any } }
>('gygEquipmentList/getEquipmentMasters', async ({ take = 100, sortBy = 'name' }, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.get(`resto365-equipment-masters`, {
      params: { take, sortBy }
    })

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const getEquipmentSupplierList = createAsyncThunk<
  EquipmentSupplierDataResponse,
  void,
  { rejectValue: any; extra: { httpClient: any } }
>('gygEquipmentList/getEquipmentSuppliers', async (_, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.get(`resto365-equipment-suppliers`)

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const getEquipmentBrandList = createAsyncThunk<
  EquipmentBrandDataResponse,
  void,
  { rejectValue: any; extra: { httpClient: any } }
>('gygEquipmentList/getEquipmentBrands', async (_, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.get(`resto365-equipment-brands`)

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const getEquipmentModelList = createAsyncThunk<
  EquipmenModelDataResponse,
  void,
  { rejectValue: any; extra: { httpClient: any } }
>('gygEquipmentList/getEquipmentModels', async (_, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.get(`resto365-equipment-models`)

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const createEquipment = createAsyncThunk<
  EquipmentResponse,
  CreateEquipment,
  { rejectValue: any; extra: { httpClient: any } }
>('gygEquipmentList/createEquipment', async (formData, { extra, rejectWithValue }) => {
  try {
    const response = await extra.httpClient.post(`/resto365-equipments`, formData)

    return response.data
  } catch (error: any) {
    if (error.response) {
      return rejectWithValue(error.response.data)
    }
    throw error
  }
})

export const updateEquipment = createAsyncThunk<EquipmentResponseById, UpdateEquipmentData, Redux>(
  'gygEquipmentList/updateEquipment',
  async ({ id, data }, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.patch(`resto365-equipments/${id}`, data)

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
      throw error
    }
  }
)

export const deleteEquipment = createAsyncThunk<EquipmentResponseById, number, Redux>(
  'gygEquipmentList/deleteEquipment',
  async (id: number, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`resto365-equipments/${id}`, { method: 'DELETE' })

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
      throw error
    }
  }
)

export const getContactEquipmentList = createAsyncThunk<EquipmentContactResponse, number[], Redux>(
  'gygEquipmentList/getContactEquipmentList',
  async (data, { rejectWithValue, extra }) => {
    try {
      const response = await extra.httpClient.get(`resto365-equipment-supplier-contacts?supplierIds=[${data}]`)

      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const createContactEquipment = createAsyncThunk<
  EquipmentContactResponse,
  EquipmentContact,
  { rejectValue: any; extra: { httpClient: any } }
>('gygEquipmentList/createContactEquipment', async (formData, { extra, rejectWithValue }) => {
  try {
    const response = await extra.httpClient.post(`/resto365-equipment-supplier-contacts`, formData)

    return response.data
  } catch (error: any) {
    if (error.response) {
      return rejectWithValue(error.response.data)
    }
    throw error
  }
})

export const updateContactEquipment = createAsyncThunk<EquipmentContactResponseById, UpdateEquipmentContactData, Redux>(
  'gygEquipmentList/updateContactEquipment',
  async ({ id, data }, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.patch(`resto365-equipment-supplier-contacts/${id}`, data)

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
      throw error
    }
  }
)

export const deleteContactEquipment = createAsyncThunk<EquipmentContactResponseById, number, Redux>(
  'gygEquipmentList/deleteContactEquipment',
  async (id: number, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`resto365-equipment-supplier-contacts/${id}`, { method: 'DELETE' })

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
      throw error
    }
  }
)

const gygEquipmentListSlice = createSlice({
  name: 'gygEquipmentList',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getEquipmentList.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getEquipmentList.fulfilled, (state, action) => {
        state.loading = false
        state.success = null
        state.equipment = null
        state.equipmentList = action.payload.data
        state.contactList = []
      })
      .addCase(getEquipmentList.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to get equipment list.'
      })

      .addCase(getEquipmentCategoryList.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getEquipmentCategoryList.fulfilled, (state, action) => {
        state.loading = false
        state.categoryList = action.payload.data
      })
      .addCase(getEquipmentCategoryList.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to get category list.'
      })

      .addCase(getEquipmentMasterList.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getEquipmentMasterList.fulfilled, (state, action) => {
        state.loading = false
        state.masterEquipmentList = action.payload.data
      })
      .addCase(getEquipmentMasterList.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to get equipment list.'
      })

      .addCase(getEquipmentSupplierList.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getEquipmentSupplierList.fulfilled, (state, action) => {
        state.loading = false
        state.supplierList = action.payload.data
      })
      .addCase(getEquipmentSupplierList.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to get equipment list.'
      })

      .addCase(getEquipmentBrandList.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getEquipmentBrandList.fulfilled, (state, action) => {
        state.loading = false
        state.brandList = action.payload.data
      })
      .addCase(getEquipmentBrandList.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to get brand list.'
      })

      .addCase(getEquipmentModelList.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getEquipmentModelList.fulfilled, (state, action) => {
        state.loading = false
        state.modelList = action.payload.data
      })
      .addCase(getEquipmentModelList.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to get brand list.'
      })

      .addCase(createEquipment.fulfilled, (state, action) => {
        state.equipmentList = action.payload.data
        state.equipment = null
        state.success = 'Equipment Created Successfully.'
        state.error = null
        state.loading = false
      })
      .addCase(createEquipment.pending, state => {
        state.loading = true
      })
      .addCase(createEquipment.rejected, state => {
        state.loading = false
      })

      .addCase(updateEquipment.fulfilled, (state, action) => {
        state.equipment = action.payload.data
        state.success = 'Equipment Updated Successfully.'
        state.error = null
        state.loading = false
      })
      .addCase(updateEquipment.pending, state => {
        state.loading = true
      })
      .addCase(updateEquipment.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to update equipment list.'
      })

      .addCase(deleteEquipment.fulfilled, (state, action) => {
        state.equipment = action.payload.data
        state.success = 'Equipment Deleted Successfully.'
        state.error = null
        state.loading = false
      })
      .addCase(deleteEquipment.pending, state => {
        state.loading = true
      })
      .addCase(deleteEquipment.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to delete equipment list.'
      })

      .addCase(getContactEquipmentList.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getContactEquipmentList.fulfilled, (state, action) => {
        state.loading = false
        state.success = null
        state.contact = null
        state.contactList = action.payload.data
      })
      .addCase(getContactEquipmentList.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to get supplier contact list.'
      })

      .addCase(createContactEquipment.fulfilled, (state, action) => {
        state.contactList = action.payload.data
        state.contact = null
        state.success = 'Supplier Contact Created Successfully.'
        state.error = null
        state.loading = false
      })
      .addCase(createContactEquipment.pending, state => {
        state.loading = true
      })
      .addCase(createContactEquipment.rejected, state => {
        state.loading = false
      })

      .addCase(updateContactEquipment.fulfilled, (state, action) => {
        state.contact = action.payload.data
        state.success = 'Supplier Contact Updated Successfully.'
        state.error = null
        state.loading = false
      })
      .addCase(updateContactEquipment.pending, state => {
        state.loading = true
      })
      .addCase(updateContactEquipment.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to update supplier contact.'
      })

      .addCase(deleteContactEquipment.fulfilled, (state, action) => {
        state.contact = action.payload.data
        state.success = 'Equipment Deleted Successfully.'
        state.error = null
        state.loading = false
      })
      .addCase(deleteContactEquipment.pending, state => {
        state.loading = true
      })
      .addCase(deleteContactEquipment.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to delete supplier contact.'
      })
  }
})

export default gygEquipmentListSlice.reducer
